// ─── Design Tokens ───────────────────────────────────────────────
// Single source of truth for all shared styling values.
// Consumed by theme.js (MUI) and synced manually to _variables.scss (Bootstrap).
// ─────────────────────────────────────────────────────────────────

// Palette catalog lives in its own file so designers can edit it
// without touching the resolution / random-generation logic.
import { palettes } from "./palettes";

export { palettes };

export const PALETTE_STORAGE_KEY = "rnc.palette";
export const RANDOM_PALETTE_STORAGE_KEY = "rnc.palette.random";
export const RANDOM_PALETTE_ID = "__random__";
export const DEFAULT_PALETTE = "jewelGreen";

// ─── Random palette generation ───────────────────────────────────
// HSL → hex helper. h ∈ [0, 360), s/l ∈ [0, 100].
function hslToHex(h, s, l) {
  s /= 100;
  l /= 100;
  const k = (n) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n) =>
    Math.round(255 * (l - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1))))
      .toString(16)
      .padStart(2, "0");
  return `#${f(0)}${f(8)}${f(4)}`.toUpperCase();
}

function hexToHsl(hex) {
  const h = (hex || "#000000").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let hh = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        hh = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        hh = (b - r) / d + 2;
        break;
      default:
        hh = (r - g) / d + 4;
    }
    hh /= 6;
  }
  return [hh * 360, s * 100, l * 100];
}

// Bootstrap 5 defaults — fixed semantic colors (theme-independent).
const BOOTSTRAP_SEMANTIC = {
  error: "#DC3545",
  warning: "#FFC107",
  success: "#198754",
  info: "#0DCAF0",
};

// Blend two hex colors. weightB ∈ [0,1] — share of hexB in the result.
function mixTwoColors(hexA, hexB, weightB) {
  const parse = (hex) => {
    const h = (hex || "#000000").replace("#", "");
    const full =
      h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
    return [
      parseInt(full.slice(0, 2), 16),
      parseInt(full.slice(2, 4), 16),
      parseInt(full.slice(4, 6), 16),
    ];
  };
  const [r1, g1, b1] = parse(hexA);
  const [r2, g2, b2] = parse(hexB);
  const w = Math.min(Math.max(weightB, 0), 1);
  const mix = (a, b) => Math.round(a * (1 - w) + b * w);
  const toHex = (n) => n.toString(16).padStart(2, "0");
  return `#${toHex(mix(r1, r2))}${toHex(mix(g1, g2))}${toHex(mix(b1, b2))}`.toUpperCase();
}

function hueDist(a, b) {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

function deriveHighlight(palette) {
  if (palette.highlight) return palette.highlight;

  // Lime-style legacy: chromatic secondary doubles as the third brand color.
  if (isChromaticBrandColor(palette.secondary)) return palette.secondary;

  const [pH, pS, pL] = hexToHsl(palette.primary);
  const [aH] = hexToHsl(palette.accent);
  const candidates = [
    (pH + 120) % 360,
    (pH + 240) % 360,
    (aH + 90) % 360,
    (aH + 270) % 360,
  ];
  const highlightH =
    candidates.find(
      (ch) => hueDist(ch, pH) >= 32 && hueDist(ch, aH) >= 32,
    ) ?? (pH + 120) % 360;

  return hslToHex(
    highlightH,
    Math.min(Math.max(pS, 40), 72),
    Math.min(Math.max(pL + 22, 48), 68),
  );
}

function resolveHighlightHover(palette, highlight) {
  if (palette.highlightHover) return palette.highlightHover;
  const [h, s, l] = hexToHsl(highlight);
  return hslToHex(h, s, Math.max(l - 8, 30));
}

function pickHighlightHue(primaryH, accentH, scheme) {
  const minSep = 32;
  const schemeCandidates = {
    triadic: [(primaryH + 240) % 360],
    analogous: [(primaryH + (accentH >= primaryH ? 60 : -60) + 360) % 360],
    monochromatic: [(primaryH + 180) % 360, (primaryH + 45) % 360],
    complementary: [(primaryH + 90) % 360, (primaryH + 270) % 360],
    splitComplementary: [(primaryH + 300) % 360, (primaryH + 60) % 360],
  };
  const candidates = [
    ...(schemeCandidates[scheme] ?? []),
    (primaryH + 120) % 360,
    (primaryH + 240) % 360,
    (accentH + 90) % 360,
    (accentH + 270) % 360,
  ];

  for (const raw of candidates) {
    const ch = ((raw % 360) + 360) % 360;
    if (hueDist(ch, primaryH) >= minSep && hueDist(ch, accentH) >= minSep) {
      return ch;
    }
  }
  return (primaryH + 120) % 360;
}

function isChromaticBrandColor(hex) {
  if (!hex) return false;
  const [, s, l] = hexToHsl(hex);
  return s >= 18 && l >= 28 && l <= 82;
}

function brandSectionWash(sourceHex, palette, { bgShare = 0.88, minSat = 28 } = {}) {
  const bg = palette.background ?? "#FFFFFF";
  const mixed = mixTwoColors(sourceHex, bg, bgShare);
  const [srcH, srcS] = hexToHsl(sourceHex);
  const [, mixedS, mixedL] = hexToHsl(mixed);

  // Blend into page bg, then keep enough saturation to read as tinted — not flat gray.
  const sOut = Math.min(Math.max(mixedS, minSat, srcS * 0.32), 40);
  const lOut = Math.min(Math.max(mixedL, 91.5), 94.5);

  return hslToHex(srcH, sOut, lOut);
}

function hueDegrees(hex) {
  return hexToHsl(hex)[0];
}

function pickDistinctSources(palette) {
  const highlight = deriveHighlight(palette);
  const ordered = [
    palette.primary,
    highlight,
    palette.accent,
    palette.primaryLight,
    isChromaticBrandColor(palette.secondary) &&
    palette.secondary !== highlight
      ? palette.secondary
      : null,
  ].filter(Boolean);

  const picked = [];
  const pickedHues = [];

  for (const hex of ordered) {
    const h = hueDegrees(hex);
    const tooClose = pickedHues.some(
      (existing) => Math.min(Math.abs(h - existing), 360 - Math.abs(h - existing)) < 28,
    );
    if (tooClose && picked.length) continue;
    picked.push(hex);
    pickedHues.push(h);
    if (picked.length === 3) break;
  }

  while (picked.length < 3) {
    const [pH] = hexToHsl(palette.primary);
    picked.push(hslToHex((pH + picked.length * 40) % 360, 28, 72));
  }

  return picked;
}

// Build 3 distinct soft section tints for a palette. Used so pages with
// multiple stacked sections don't all share the same background tone.
// Priority: explicit `sectionTints` array on the palette → derive from
// well-separated brand hues blended into the page background.
function buildSectionTints(palette) {
  if (Array.isArray(palette.sectionTints) && palette.sectionTints.length >= 3) {
    return palette.sectionTints.slice(0, 3);
  }

  const sources = pickDistinctSources(palette);
  const profiles = [
    { bgShare: 0.91, minSat: 26 }, // primary
    { bgShare: 0.87, minSat: 30 }, // highlight (third brand)
    { bgShare: 0.84, minSat: 32 }, // accent
  ];

  return sources.map((hex, i) =>
    brandSectionWash(hex, palette, profiles[i] ?? profiles[2]),
  );
}

/**
 * Generate a cohesive random palette using a single base hue plus a
 * complementary accent. Returns a palette object with the same shape as
 * entries in `palettes` above.
 */
/**
 * Generate a cohesive random palette. Each roll picks ONE of several
 * color schemes (relationship between primary and accent), ONE of
 * several moods (lightness/saturation profile), and ONE background
 * style — so successive rolls feel like genuinely different palettes,
 * not just hue rotations of the same one.
 *
 * Returns a palette object with the same shape as entries in `palettes`,
 * plus a `_meta` field describing which scheme/mood/bg style was chosen.
 */
export function generateRandomPalette() {
  const rand = (min, max) => min + Math.random() * (max - min);
  const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
  const jitter = (n) => rand(-n, n);

  const scheme = pick([
    "complementary",
    "analogous",
    "triadic",
    "splitComplementary",
    "monochromatic",
  ]);
  const mood = pick(["pastel", "jewel", "earthy", "vivid", "noir"]);
  const bgStyle = pick(["tinted", "neutral", "cream", "coolGrey"]);

  const h = Math.floor(Math.random() * 360);

  // Accent hue depends on the chosen color scheme.
  let accentH;
  switch (scheme) {
    case "analogous":
      accentH = (h + (Math.random() < 0.5 ? 30 : -30) + jitter(10) + 360) % 360;
      break;
    case "triadic":
      accentH = (h + 120 + jitter(15)) % 360;
      break;
    case "splitComplementary":
      accentH = (h + (Math.random() < 0.5 ? 150 : 210) + jitter(10)) % 360;
      break;
    case "monochromatic":
      accentH = h;
      break;
    case "complementary":
    default:
      accentH = (h + 180 + jitter(20)) % 360;
  }

  // Lightness/saturation profile per mood.
  const M = {
    pastel: {
      primary: [rand(35, 50), rand(45, 55)],
      accent: [rand(40, 55), rand(72, 82)],
      highlight: [rand(42, 58), rand(62, 76)],
      chrome: [rand(30, 45), rand(35, 45)],
      onChrome: [rand(20, 30), rand(90, 95)],
      text: [rand(20, 30), rand(15, 22)],
      section: [rand(30, 45), rand(90, 94)],
    },
    jewel: {
      primary: [rand(60, 80), rand(25, 35)],
      accent: [rand(65, 80), rand(55, 68)],
      highlight: [rand(55, 75), rand(48, 62)],
      chrome: [rand(55, 75), rand(12, 20)],
      onChrome: [rand(30, 45), rand(88, 94)],
      text: [rand(25, 35), rand(10, 16)],
      section: [rand(35, 50), rand(88, 94)],
    },
    earthy: {
      primary: [rand(25, 40), rand(38, 50)],
      accent: [rand(35, 55), rand(58, 72)],
      highlight: [rand(30, 50), rand(52, 68)],
      chrome: [rand(25, 40), rand(28, 38)],
      onChrome: [rand(20, 30), rand(88, 93)],
      text: [rand(20, 30), rand(15, 22)],
      section: [rand(25, 40), rand(88, 93)],
    },
    vivid: {
      primary: [rand(70, 90), rand(40, 50)],
      accent: [rand(75, 95), rand(55, 70)],
      highlight: [rand(65, 88), rand(52, 66)],
      chrome: [rand(60, 80), rand(15, 25)],
      onChrome: [rand(25, 40), rand(90, 95)],
      text: [rand(25, 35), rand(10, 18)],
      section: [rand(40, 60), rand(90, 94)],
    },
    noir: {
      primary: [rand(40, 60), rand(38, 48)],
      accent: [rand(55, 75), rand(60, 72)],
      highlight: [rand(45, 65), rand(55, 68)],
      chrome: [rand(30, 50), rand(6, 12)],
      onChrome: [rand(25, 40), rand(85, 92)],
      text: [rand(20, 35), rand(8, 14)],
      section: [rand(25, 40), rand(85, 92)],
    },
  }[mood];

  const highlightH = pickHighlightHue(h, accentH, scheme);

  // Background hue/sat/lightness per bg style.
  let bgH = h;
  let bgS;
  let bgL;
  switch (bgStyle) {
    case "neutral":
      bgH = 0;
      bgS = rand(0, 6);
      bgL = rand(96, 98);
      break;
    case "cream":
      bgH = rand(30, 45);
      bgS = rand(30, 45);
      bgL = rand(93, 96);
      break;
    case "coolGrey":
      bgH = rand(200, 230);
      bgS = rand(8, 18);
      bgL = rand(93, 96);
      break;
    case "tinted":
    default:
      bgS = rand(20, 35);
      bgL = rand(94, 97);
  }

  // Section tint hue can come from the primary, accent, or a triad —
  // adds variety even within the same scheme/mood combo.
  const sectionH = pick([h, accentH, (h + 120) % 360]);

  return {
    primary: hslToHex(h, M.primary[0], M.primary[1]),
    primaryLight: hslToHex(h, M.primary[0] * 0.85, Math.min(M.primary[1] + 20, 75)),
    accent: hslToHex(accentH, M.accent[0], M.accent[1]),
    accentHover: hslToHex(accentH, M.accent[0], Math.max(M.accent[1] - 10, 30)),
    highlight: hslToHex(highlightH, M.highlight[0], M.highlight[1]),
    highlightHover: hslToHex(
      highlightH,
      M.highlight[0],
      Math.max(M.highlight[1] - 10, 32),
    ),
    background: hslToHex(bgH, bgS, bgL),
    surface: "#FFFFFF",
    sectionTint: hslToHex(sectionH, M.section[0], M.section[1]),
    secondary: hslToHex(h, M.text[0], M.text[1]),
    text: hslToHex(h, M.text[0], M.text[1]),
    brandSurface: hslToHex(h, M.chrome[0], M.chrome[1]),
    onBrandSurface: hslToHex(h, M.onChrome[0], M.onChrome[1]),
    _meta: { scheme, mood, bgStyle, h: Math.round(h), highlightH: Math.round(highlightH) },
  };
}

// Inject any persisted random palette into `palettes` so the rest of the
// resolution logic treats it like any named palette.
if (typeof window !== "undefined") {
  try {
    const raw = window.localStorage.getItem(RANDOM_PALETTE_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && parsed.primary) {
        palettes[RANDOM_PALETTE_ID] = parsed;
      }
    }
  } catch {
    /* ignore parse / storage errors */
  }
}

/**
 * Resolve which palette to use, in priority order:
 *   1. `?palette=NAME` URL query param (also persisted to localStorage)
 *   2. localStorage[PALETTE_STORAGE_KEY]
 *   3. DEFAULT_PALETTE
 * Unknown palette names fall back to DEFAULT_PALETTE.
 * Non-admins are always pinned to DEFAULT_PALETTE — admin status is
 * read directly from localStorage since Redux is not available at
 * module-load time.
 * Safe to call during module init (handles SSR / no-window).
 */
export function getActivePaletteName() {
  if (typeof window === "undefined") return DEFAULT_PALETTE;
  try {
    const isAdmin = window.localStorage.getItem("is_admin") === "true";
    if (!isAdmin) return DEFAULT_PALETTE;
    const url = new URL(window.location.href);
    const fromQuery = url.searchParams.get("palette");
    if (fromQuery && palettes[fromQuery]) {
      window.localStorage.setItem(PALETTE_STORAGE_KEY, fromQuery);
      return fromQuery;
    }
    const stored = window.localStorage.getItem(PALETTE_STORAGE_KEY);
    if (stored && palettes[stored]) return stored;
  } catch {
    /* localStorage may be blocked */
  }
  return DEFAULT_PALETTE;
}

const activePaletteName = getActivePaletteName();
const activePalette = palettes[activePaletteName];
const resolvedHighlight = deriveHighlight(activePalette);
const resolvedHighlightHover = resolveHighlightHover(
  activePalette,
  resolvedHighlight,
);
const sectionTints = buildSectionTints(activePalette);

function resolveOutlinedBrandColors(palette, highlight, highlightHover) {
  if (isChromaticBrandColor(palette.secondary)) {
    return {
      main: palette.secondary,
      hover: palette.secondaryHover ?? palette.secondary,
    };
  }
  if (isChromaticBrandColor(highlight)) {
    return { main: highlight, hover: highlightHover };
  }
  return null;
}

const outlinedBrand = resolveOutlinedBrandColors(
  activePalette,
  resolvedHighlight,
  resolvedHighlightHover,
);

// ─── Active palette ──────────────────────────────────────────────
// Brand colors come from the active palette; semantic colors are fixed Bootstrap defaults.
export const colors = {
  // Brand (palette-driven)
  primary: activePalette.primary,
  primaryLight: activePalette.primaryLight,
  accent: activePalette.accent,
  accentHover: activePalette.accentHover,
  secondary: activePalette.secondary,
  secondaryHover: activePalette.secondaryHover ?? activePalette.secondary,
  highlight: resolvedHighlight,
  highlightHover: resolvedHighlightHover,
  // True when secondary is a chromatic brand accent (legacy lime palettes).
  secondaryBrandAccent: isChromaticBrandColor(activePalette.secondary),
  // Third brand color — explicit or derived; used for section tints etc.
  highlightBrandAccent: isChromaticBrandColor(resolvedHighlight),
  outlinedBrandMain: outlinedBrand?.main ?? null,
  outlinedBrandHover: outlinedBrand?.hover ?? null,

  // Surfaces (palette-driven)
  background: activePalette.background,
  surface: activePalette.surface,

  // Brand surface — used by navbar / footer / drawer.
  // Falls back to primary (dark palette) when not explicitly set (light palette).
  brandSurface: activePalette.brandSurface ?? activePalette.primary,
  onBrandSurface: activePalette.onBrandSurface ?? "#FFFFFF",

  // Section tint — soft tinted background for featured sections, callouts.
  // `sectionTint` is the primary tint; `sectionTints` is an array of 3
  // cohesive but distinct tints so stacked sections don't repeat.
  sectionTint: sectionTints[0],
  sectionTints,

  // Text (palette-driven)
  textPrimary: activePalette.text,

  // Supplementary brand (from _variables.scss)
  peach: "#FFE5B8",
  lightGreen: "#c2c9a2",
  ebonyGreen: "#5A6650",

  // Semantic — Bootstrap defaults (theme-independent)
  ...BOOTSTRAP_SEMANTIC,

  // Neutral text (stable across palettes)
  textSecondary: "#6b7280",
  textMuted: "#9aa5a0",

  // Auxiliary surfaces (stable across palettes)
  surfaceSoft: "#f4f8f2",
  surfaceMuted: "#eef2f7",
  surfacePink: "#FBEFF1",
  surfaceWarm: "#fdfdfe",

  // Borders
  footerBorder: "#d6c8ba",
  foodlistBorder: "#dce9d6",

  // Overlays
  overlayLight: "rgba(0,0,0,0.06)",
  overlayMedium: "rgba(0,0,0,0.12)",

  // Chart — derived from the active palette (see deriveChartPalette).
  // The Forest palette declares `chart` explicitly; other palettes get
  // a cohesive ramp built from primary + accent.
  ...chartColors(activePalette),
};

// Convenience exports — useful for a palette switcher UI.
export const activePaletteId = activePaletteName;
export const paletteIds = Object.keys(palettes);

// ─── Tint helper ─────────────────────────────────────────────────
// Convert a hex string to an rgba string with the given alpha.
export function tint(hex, alpha = 0.12) {
  const h = (hex || "#000000").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ─── Derived: nutrient-page palette ──────────────────────────────
// Given the active brand palette, build a matching three-color scheme
// for the nutrient explorer (Vitaminer / Mineraler / Makro). The brand
// primary + accent become two of the three category colors; the third
// is derived by rotating the primary hue and reaching for a clearly
// distinct neighbor (~150° away, avoiding collisions with primary/accent).
export function deriveNutrientPalette(palette) {
  const primary = palette.primary;
  const accent = palette.accent;
  const [ph, ps, pl] = hexToHsl(primary);
  const [ah] = hexToHsl(accent);

  // Pick a hue ~150° from primary; if it lands near the accent, push
  // it the other way so all three category colors stay distinguishable.
  let thirdHue = (ph + 150) % 360;
  const hueDist = Math.min(
    Math.abs(thirdHue - ah),
    360 - Math.abs(thirdHue - ah),
  );
  if (hueDist < 40) thirdHue = (ph - 150 + 360) % 360;

  // Keep saturation/lightness in a comfortable mid-range so it reads
  // as a sibling of primary, not a screaming outlier.
  const thirdS = Math.min(Math.max(ps, 45), 70);
  const thirdL = Math.min(Math.max(pl + 5, 42), 55);
  const makro = hslToHex(thirdHue, thirdS, thirdL);

  return {
    vitamin: accent,
    mineral: primary,
    makro,
    accent,
    heroFrom: primary,
    heroTo: accent,
    blobA: tint(accent, 0.22),
    blobB: tint(primary, 0.18),
    ring: primary,
  };
}

// ─── Derived: chart palette (macro / micro bars) ─────────────────
// Returns { macro, micro, microAccent } matching the shape used by
// MacroBars/MicroBars. Honors palette.chart when present (Forest);
// otherwise builds a cohesive ramp from primary + accent.
export function deriveChartPalette(palette) {
  if (palette && palette.chart) return palette.chart;

  const primary = palette.primary;
  const accent = palette.accent;
  const [ph, ps] = hexToHsl(primary);
  const [, as] = hexToHsl(accent);

  // Four evenly-spaced hues anchored on primary so each micro group
  // is visually distinct regardless of how close primary/accent are.
  // Order: minerals (primary), vitamins (+90°), fat (+180°), other (+270°).
  const mineralsH = ph;
  const vitaminsH = (ph + 90) % 360;
  const fatH = (ph + 180) % 360;
  const otherH = (ph + 270) % 360;

  // Use a consistent mid-saturation that doesn't depend on whether the
  // brand color is washed-out or saturated. Slightly vary lightness per
  // group as an extra differentiation cue.
  const baseSat = Math.min(Math.max((ps + as) / 2, 50), 65);

  // Build a 5-step lightest→darkest ramp around a target hue.
  const buildRamp = (hue, sat, baseL) => {
    const targetL = Math.min(Math.max(baseL, 40), 60);
    const lightL = Math.min(targetL + 30, 88);
    const steps = 5;
    return Array.from({ length: steps }, (_, i) => {
      const t = i / (steps - 1); // 0 (light) → 1 (dark)
      const l = lightL + (targetL - lightL) * t;
      return hslToHex(hue, sat, l);
    });
  };

  const mineralsRamp = buildRamp(mineralsH, baseSat, 48);
  const vitaminsRamp = buildRamp(vitaminsH, baseSat, 54);
  const fatRamp = buildRamp(fatH, baseSat, 50);
  const otherRamp = buildRamp(otherH, baseSat, 56);

  // Macro chart: stable neutral for energy, four distinct hues for macros.
  return {
    macro: {
      energy: "#6B7280",
      protein: hslToHex(mineralsH, baseSat, 50),
      carbs: hslToHex(fatH, baseSat, 60),
      fat: hslToHex(vitaminsH, baseSat, 58),
    },
    micro: {
      vitamins: vitaminsRamp,
      minerals: mineralsRamp,
      fat: fatRamp,
      other: otherRamp,
    },
    microAccent: {
      vitamins: vitaminsRamp[4],
      minerals: mineralsRamp[4],
      fat: fatRamp[4],
      other: otherRamp[4],
    },
  };
}

function chartColors(palette) {
  return deriveChartPalette(palette);
}

// ─── Typography ──────────────────────────────────────────────────
export const fonts = {
  heading: '"Fraunces Variable", "Fraunces", Georgia, serif',
  body: '"Inter", system-ui, sans-serif',
  mono: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  handwritten: '"Grape Nuts", cursive',
};

// ─── Spacing (4 px grid) ─────────────────────────────────────────
export const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
};

// ─── Border radii ────────────────────────────────────────────────
export const radii = {
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  pill: 999,
  circle: "50%",
};

// ─── Shadows ─────────────────────────────────────────────────────
export const shadows = {
  sm: "0 2px 6px rgba(0,0,0,0.08)",
  md: "0 6px 20px rgba(0,0,0,0.06)",
  lg: "0 10px 30px rgba(20,32,30,0.10)",
  xl: "0 18px 40px rgba(20,32,30,0.12)",
};

// ─── Breakpoints ─────────────────────────────────────────────────
export const breakpoints = {
  xxs: 0,
  xs: 360,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
  xxxl: 1600,
};
